/**
 * Frequency Role - Format Pillar
 *
 * Formatação de valores de frequência para exibição.
 *
 * @example
 * import { formatFrequency, getFrequencySymbol, getFrequencyName } from '@attrx/role-morphic/frequency/format';
 *
 * formatFrequency('gigahertz', 2.4);                     // "2.4 GHz"
 * formatFrequency('gigahertz', 2.4, { verbose: true });  // "2.4 gigahertz"
 * getFrequencySymbol('megahertz');                       // "MHz"
 * getFrequencyName('hertz', 2);                          // "hertz"
 */

import { FREQUENCY_UNITS, type FrequencyUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface FrequencyFormatOptions {
  /** Número de casas decimais */
  decimals?: number;
  /** Usar nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação numérica */
  locale?: string;
  /** Notação: standard, scientific, compact */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de frequência para exibição
 *
 * @param variant - Unidade
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatFrequency('gigahertz', 2.4);                     // "2.4 GHz"
 * formatFrequency('gigahertz', 2.4, { verbose: true });  // "2.4 gigahertz"
 * formatFrequency('hertz', 1, { verbose: true });        // "1 hertz"
 * formatFrequency('megahertz', 100, { decimals: 0 });    // "100 MHz"
 */
export function formatFrequency(
  variant: FrequencyUnit,
  value: number,
  options: FrequencyFormatOptions = {},
): string {
  const config = FREQUENCY_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown frequency unit: ${variant}`);
  }

  const { decimals, verbose = false, locale, notation = "standard" } = options;

  // Format number
  let formattedNumber: string;

  if (locale || notation !== "standard") {
    const intlOptions: Intl.NumberFormatOptions = { notation };
    if (decimals !== undefined) {
      intlOptions.minimumFractionDigits = decimals;
      intlOptions.maximumFractionDigits = decimals;
    }
    formattedNumber = new Intl.NumberFormat(locale, intlOptions).format(value);
  } else if (decimals !== undefined) {
    formattedNumber = value.toFixed(decimals);
  } else {
    // Smart formatting: remove trailing zeros
    formattedNumber = Number(value.toPrecision(12)).toString();
  }

  // Get unit label
  const unitLabel = verbose
    ? value === 1
      ? config.singular
      : config.plural
    : config.symbol;

  return `${formattedNumber} ${unitLabel}`;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna o símbolo de uma unidade
 *
 * @example
 * getFrequencySymbol('megahertz');  // "MHz"
 * getFrequencySymbol('rpm');        // "rpm"
 */
export function getFrequencySymbol(variant: FrequencyUnit): string {
  const config = FREQUENCY_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown frequency unit: ${variant}`);
  }
  return config.symbol;
}

/**
 * Retorna o nome de uma unidade (singular ou plural)
 *
 * @param variant - Unidade
 * @param count - Quantidade para determinar singular/plural (default: 1)
 *
 * @example
 * getFrequencyName('hertz', 1);  // "hertz"
 * getFrequencyName('hertz', 2);  // "hertz"
 * getFrequencyName('rpm', 2);    // "revolutions per minute"
 */
export function getFrequencyName(variant: FrequencyUnit, count: number = 1): string {
  const config = FREQUENCY_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown frequency unit: ${variant}`);
  }
  return count === 1 ? (config.singular || config.symbol) : (config.plural || config.symbol);
}
